import { z } from "zod";
import {
  ContentFitnessAssessmentModeSchema,
  ContentFitnessVerificationStateSchema,
} from "../../enums/content-fitness";
import { CrawlRunStatusSchema } from "../../enums/core";
import { PageAnalysisStatusSchema } from "../../enums/page-analysis";
import { ScreenshotDeviceSchema } from "../../enums/scrape";
import {
  CheckJudgmentSchema,
  YeetAttemptFailureReasonSchema,
  YeetAttemptStatusSchema,
} from "../../enums/yeet";
import {
  RunFindingSchema,
  YeetWorkerStatusSchema,
} from "../crawl/run-read.responses";
import { FindingEvidenceSchema } from "../yeet/tools/assessment";
import { ReportLoopUsageInputSchema } from "../yeet/tools/completion";

export * from "./draft";

// Admin yeet-playground surface (docs plan "Yeet Playground"). A workspace is
// one playground-kind client — one brand voice, its single-page websites, and
// their run history. Runs are regular yeet crawl runs pinned at creation;
// this API only shapes their inputs and re-presents their regular output.

export const PlaygroundWorkspaceCreateRequestSchema = z.object({
  name: z.string().min(1),
});
export type PlaygroundWorkspaceCreateRequest = z.infer<
  typeof PlaygroundWorkspaceCreateRequestSchema
>;

export const PlaygroundWebsiteSchema = z.object({
  websiteId: z.uuid(),
  originUrl: z.string(),
});
export type PlaygroundWebsite = z.infer<typeof PlaygroundWebsiteSchema>;

export const PlaygroundWorkspaceSchema = z.object({
  clientId: z.uuid(),
  name: z.string(),
  createdAt: z.coerce.date(),
  // Active TOV version pinned onto new runs; null until a voice is activated.
  tovVersionId: z.uuid().nullable(),
  websites: z.array(PlaygroundWebsiteSchema),
});
export type PlaygroundWorkspace = z.infer<typeof PlaygroundWorkspaceSchema>;

export const PlaygroundWorkspaceListResponseSchema = z.object({
  workspaces: z.array(PlaygroundWorkspaceSchema),
});
export type PlaygroundWorkspaceListResponse = z.infer<
  typeof PlaygroundWorkspaceListResponseSchema
>;

export const PlaygroundUrlCreateRequestSchema = z.object({
  originUrl: z.url(),
});
export type PlaygroundUrlCreateRequest = z.infer<
  typeof PlaygroundUrlCreateRequestSchema
>;

export const PlaygroundUrlCreateResponseSchema = z.object({
  websiteId: z.uuid(),
});
export type PlaygroundUrlCreateResponse = z.infer<
  typeof PlaygroundUrlCreateResponseSchema
>;

export const PlaygroundWorkspaceDeleteResponseSchema = z.object({
  deleted: z.literal(true),
});
export type PlaygroundWorkspaceDeleteResponse = z.infer<
  typeof PlaygroundWorkspaceDeleteResponseSchema
>;

export const PlaygroundRunCreateRequestSchema = z.object({
  // Catalog draft to pin on the run; omitted/null = canonical catalog.
  catalogDraftId: z.uuid().nullable().optional(),
  // Yeet-agent prompt bundle to pin on the run; omitted/null = the surface's
  // active bundle (same resolution every production run uses).
  promptBundleId: z.uuid().nullable().optional(),
  // Analysis model for this run only, stored as the run's yeet config override
  // and merged over the AppConfig-resolved config on every analysis publish.
  // Omitted = the resolved `config-yeet` model, which is every production run.
  agentModel: z.string().min(1).optional(),
});
export type PlaygroundRunCreateRequest = z.infer<
  typeof PlaygroundRunCreateRequestSchema
>;

export const PlaygroundRunCreateResponseSchema = z.object({
  runId: z.uuid(),
});
export type PlaygroundRunCreateResponse = z.infer<
  typeof PlaygroundRunCreateResponseSchema
>;

export const PlaygroundFindingCountsSchema = z.object({
  critical: z.number().int().min(0),
  high: z.number().int().min(0),
  medium: z.number().int().min(0),
  low: z.number().int().min(0),
});
export type PlaygroundFindingCounts = z.infer<
  typeof PlaygroundFindingCountsSchema
>;

export const PlaygroundRunSummarySchema = z.object({
  runId: z.uuid(),
  websiteId: z.uuid(),
  status: CrawlRunStatusSchema,
  startedAt: z.coerce.date(),
  finishedAt: z.coerce.date().nullable(),
  error: z.string().nullable(),
  // TOV version, catalog draft, and prompt bundle the run pinned at creation;
  // together they are the experiment key that distinguishes two runs.
  tovVersionId: z.uuid().nullable(),
  catalogDraftId: z.uuid().nullable(),
  catalogDraftLabel: z.string().nullable(),
  promptBundleId: z.uuid().nullable(),
  promptBundleLabel: z.string().nullable(),
  findingCounts: PlaygroundFindingCountsSchema,
  // Consolidated page scores; null until the run completes (or when a
  // dimension family produced nothing — absence, never a fabricated number).
  humanScore: z.number().int().nullable(),
  agentScore: z.number().int().nullable(),
});
export type PlaygroundRunSummary = z.infer<typeof PlaygroundRunSummarySchema>;

export const PlaygroundRunListResponseSchema = z.object({
  runs: z.array(PlaygroundRunSummarySchema),
});
export type PlaygroundRunListResponse = z.infer<
  typeof PlaygroundRunListResponseSchema
>;

export const PlaygroundAttemptSchema = z.object({
  attemptId: z.uuid(),
  status: YeetAttemptStatusSchema,
  // Why the attempt failed; null while running or on success.
  failureReason: YeetAttemptFailureReasonSchema.nullable(),
  // First authenticated MCP request from the sandbox; null means it never
  // reached the backend (pairs with a `no_mcp_contact` failure reason).
  firstMcpContactAt: z.coerce.date().nullable(),
  summary: z.string().nullable(),
  error: z.string().nullable(),
  exitCode: z.number().int().nullable(),
  sandboxId: z.string().nullable(),
  stdoutTail: z.string().nullable(),
  stderrTail: z.string().nullable(),
  deadlineAt: z.coerce.date(),
  createdAt: z.coerce.date(),
  finishedAt: z.coerce.date().nullable(),
  // The immutable ScrapingBee capture this attempt analysed — the experiment
  // key that tells two runs on the same URL apart when the origin's answer
  // differed between fetches.
  captureId: z.uuid(),
  // Loop effort reported by the sandbox harness; null when the sandbox never
  // got far enough to report (e.g. no MCP contact).
  loopUsage: ReportLoopUsageInputSchema.nullable(),
});
export type PlaygroundAttempt = z.infer<typeof PlaygroundAttemptSchema>;

export const PlaygroundJudgmentSchema = z.object({
  metricKey: z.string(),
  checkKey: z.string(),
  judgment: CheckJudgmentSchema,
  points: z.number().int(),
  // The check question as the run's pinned catalog served it (definition as
  // run); null for dynamic tov.* checks and checks no longer resolvable.
  question: z.string().nullable(),
  evidence: z.array(FindingEvidenceSchema).nullable(),
  rationale: z.string().nullable(),
});
export type PlaygroundJudgment = z.infer<typeof PlaygroundJudgmentSchema>;

export const PlaygroundAssessmentSchema = z.object({
  subcriterionKey: z.string(),
  parentMetricKey: z.string(),
  assessmentMode: ContentFitnessAssessmentModeSchema,
  verificationState: ContentFitnessVerificationStateSchema,
  score: z.number().int().nullable(),
  rationale: z.string(),
  evidence: z.array(FindingEvidenceSchema).nullable(),
});
export type PlaygroundAssessment = z.infer<typeof PlaygroundAssessmentSchema>;

export const PlaygroundScreenshotSchema = z.object({
  // workers.page_screenshot staging id; bytes are served by
  // GET /api/playground/runs/:runId/screenshots/:screenshotId.
  id: z.uuid(),
  device: ScreenshotDeviceSchema,
  status: PageAnalysisStatusSchema,
  error: z.string().nullable(),
  capturedAt: z.coerce.date().nullable(),
});
export type PlaygroundScreenshot = z.infer<typeof PlaygroundScreenshotSchema>;

export const PlaygroundScoreSchema = z.object({
  metricKey: z.string(),
  score: z.number().int(),
  detail: z.string().nullable(),
});
export type PlaygroundScore = z.infer<typeof PlaygroundScoreSchema>;

export const PlaygroundRunOutputResponseSchema = z.object({
  run: PlaygroundRunSummarySchema,
  url: z.string(),
  attempt: PlaygroundAttemptSchema.nullable(),
  // Yeet fleet liveness while the run is running; null once it has landed. Lets
  // the playground show "no yeet worker consuming" instead of a silent spinner
  // when a run is queued but no dispatcher is up.
  yeetWorker: YeetWorkerStatusSchema.nullable(),
  judgments: z.array(PlaygroundJudgmentSchema),
  assessments: z.array(PlaygroundAssessmentSchema),
  findings: z.array(RunFindingSchema),
  screenshots: z.array(PlaygroundScreenshotSchema),
  // Consolidated page_signals rows for this run's attempt; empty while the
  // run is still running (consolidation happens at completion).
  scores: z.array(PlaygroundScoreSchema),
});
export type PlaygroundRunOutputResponse = z.infer<
  typeof PlaygroundRunOutputResponseSchema
>;
